/**
 * NotificationDropdown Component
 *
 * Dropdown panel displaying all notifications.
 * Used on desktop as a positioned dropdown below the bell icon.
 */

import { forwardRef } from 'react';
import { NotificationItem } from './NotificationItem';
import { NotificationEmptyState } from './NotificationEmptyState';
import type { UnifiedNotification } from '../../hooks/useNotifications';
import styles from './NotificationDropdown.module.css';

interface NotificationDropdownProps {
  notifications: UnifiedNotification[];
  isLoading: boolean;
  onMarkAsRead: (id: string) => void;
  onMarkAllAsRead: () => void;
  onAcceptInvite: (id: string) => Promise<void>;
  onDeclineInvite: (id: string) => Promise<void>;
  onAcceptTrade: (id: string) => Promise<void>;
  onDeclineTrade: (id: string) => Promise<void>;
  onViewTrade?: (tradeId: string) => void;
}

export const NotificationDropdown = forwardRef<HTMLDivElement, NotificationDropdownProps>(
  function NotificationDropdown(
    {
      notifications,
      isLoading,
      onMarkAsRead,
      onMarkAllAsRead,
      onAcceptInvite,
      onDeclineInvite,
      onAcceptTrade,
      onDeclineTrade,
      onViewTrade
    },
    ref
  ) {
    const hasUnread = notifications.some(n => !n.isRead);

    const handleAction = async (
      notification: UnifiedNotification,
      actionType: 'accept' | 'decline' | 'view'
    ) => {
      if (notification.type === 'invite') {
        if (actionType === 'accept') {
          await onAcceptInvite(notification.id);
        } else if (actionType === 'decline') {
          await onDeclineInvite(notification.id);
        }
      } else if (notification.type === 'trade') {
        if (actionType === 'accept') {
          await onAcceptTrade(notification.id);
        } else if (actionType === 'decline') {
          await onDeclineTrade(notification.id);
        } else if (actionType === 'view' && onViewTrade) {
          const tradeId = notification.id.replace('trade-', '');
          onViewTrade(tradeId);
        }
      }
    };

    return (
      <div ref={ref} className={styles.dropdown} role="menu" aria-label="Notifications">
        <div className={styles.dropdownHeader}>
          <h3 className={styles.dropdownTitle}>Notifications</h3>
          {hasUnread && (
            <button
              className={styles.markAllRead}
              onClick={onMarkAllAsRead}
              disabled={isLoading}
            >
              Mark all read
            </button>
          )}
        </div>

        <div className={styles.notificationList}>
          {notifications.length === 0 ? (
            <NotificationEmptyState />
          ) : (
            notifications.map(notification => (
              <NotificationItem
                key={notification.id}
                notification={notification}
                onMarkAsRead={() => onMarkAsRead(notification.id)}
                onAccept={() => handleAction(notification, 'accept')}
                onDecline={() => handleAction(notification, 'decline')}
                onView={() => handleAction(notification, 'view')}
                isLoading={isLoading}
              />
            ))
          )}
        </div>
      </div>
    );
  }
);
